import React from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Input,
  Card,
  Select,
  Row,
  Col,
  Badge,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  TeamOutlined,
  SearchOutlined,
  CarOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useVehicleRequests } from '../hooks/useVehicleRequests';
import { VehicleRequest, VehicleRequestType } from '../../api/services/vehicleRequestService';

const { Text } = Typography;

const TYPE_COLOR: Record<VehicleRequestType, string> = {
  make: 'purple',
  model: 'blue',
};

const STATUS_COLOR: Record<string, string> = {
  pending: 'gold',
  approved: 'green',
  rejected: 'red',
};

const VehicleRequestQueue: React.FC = () => {
  const { t } = useTranslation();
  const {
    requests,
    pagination,
    filters,
    loading,
    actionLoading,
    rejectModalOpen,
    rejectionReason,
    setRejectionReason,
    fetchQueue,
    handleApprove,
    openRejectModal,
    handleReject,
    handleTableChange,
    handleFilterChange,
    setRejectModalOpen,
  } = useVehicleRequests();

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const topRequested = [...requests].sort(
    (a, b) => b.requestCount - a.requestCount
  )[0];
  const totalVotes = requests.reduce((sum, r) => sum + r.requestCount, 0);

  const columns = [
    {
      title: t('admin.vehicle_requests.col_type'),
      dataIndex: 'type',
      key: 'type',
      width: 90,
      render: (type: VehicleRequestType) => (
        <Tag color={TYPE_COLOR[type]} style={{ borderRadius: 6, fontWeight: 600, textTransform: 'capitalize' }}>
          {type}
        </Tag>
      ),
    },
    {
      title: t('admin.vehicle_requests.col_requested_name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, row: VehicleRequest) => (
        <div>
          <Text strong style={{ fontSize: 14, color: 'var(--admin-text-primary)' }}>
            {name}
          </Text>
          {row.makeName && (
            <div style={{ fontSize: 11, color: 'var(--admin-text-secondary)', marginTop: 2 }}>
              {row.makeName}
            </div>
          )}
          {row.notes && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--admin-text-secondary)',
                marginTop: 3,
                fontStyle: 'italic',
                maxWidth: 260,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              "{row.notes}"
            </div>
          )}
        </div>
      ),
    },
    {
      title: (
        <Tooltip title={t('admin.vehicle_requests.col_votes_tooltip')}>
          <Space size={4}>
            <TeamOutlined />
            <span>{t('admin.vehicle_requests.col_votes')}</span>
          </Space>
        </Tooltip>
      ),
      dataIndex: 'requestCount',
      key: 'requestCount',
      width: 90,
      sorter: (a: VehicleRequest, b: VehicleRequest) => b.requestCount - a.requestCount,
      defaultSortOrder: 'ascend' as const,
      render: (count: number) => (
        <Badge
          count={count}
          showZero
          style={{
            backgroundColor: count >= 5 ? '#FE5A1D' : count >= 2 ? '#0284c7' : '#9ca3af',
            fontWeight: 700,
          }}
        />
      ),
    },
    {
      title: t('admin.vehicle_requests.col_first_requested'),
      dataIndex: 'firstRequestedAt',
      key: 'firstRequestedAt',
      width: 130,
      render: (v: string) => (
        <span style={{ fontSize: 12, color: 'var(--admin-text-secondary)' }}>
          {v ? new Date(v).toLocaleDateString() : '—'}
        </span>
      ),
    },
    {
      title: t('admin.vehicle_requests.col_status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag
          color={STATUS_COLOR[status] ?? 'default'}
          style={{ borderRadius: 6, fontWeight: 600, textTransform: 'capitalize' }}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: t('admin.vehicle_requests.col_actions'),
      key: 'actions',
      width: 160,
      render: (_: any, row: VehicleRequest) => {
        if (row.status !== 'pending') return null;
        return (
          <Space size={6}>
            <Tooltip title={t('admin.vehicle_requests.tooltip_approve')}>
              <Button
                icon={<CheckCircleOutlined />}
                className="admin-action-approve"
                onClick={() => handleApprove(row.id)}
                loading={actionLoading}
                size="small"
              >
                {t('admin.vehicle_requests.btn_approve')}
              </Button>
            </Tooltip>
            <Tooltip title={t('admin.vehicle_requests.tooltip_reject')}>
              <Button
                icon={<CloseCircleOutlined />}
                className="admin-action-reject"
                onClick={() => openRejectModal(row)}
                size="small"
              >
                {t('admin.vehicle_requests.btn_reject')}
              </Button>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {/* Stat cards */}
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <div style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 12, boxShadow: 'var(--admin-shadow)', padding: '28px 32px' }}>
            <div style={{ fontSize: 12, color: 'var(--admin-text-secondary)', marginBottom: 10 }}>{t('admin.vehicle_requests.stat_pending')}</div>
            <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1, color: '#FE5A1D', marginBottom: 8 }}>{pendingCount}</div>
            <div style={{ fontSize: 11, color: 'var(--admin-text-secondary)' }}>{t('admin.vehicle_requests.stat_pending_desc')}</div>
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 12, boxShadow: 'var(--admin-shadow)', padding: '28px 32px' }}>
            <div style={{ fontSize: 12, color: 'var(--admin-text-secondary)', marginBottom: 10 }}>{t('admin.vehicle_requests.stat_most_requested')}</div>
            <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1, color: 'var(--admin-text-primary)', marginBottom: 8 }}>
              {topRequested ? (
                <Space size={6}>
                  <CarOutlined />
                  <span>{topRequested.name}</span>
                  <Badge count={topRequested.requestCount} style={{ backgroundColor: '#FE5A1D' }} />
                </Space>
              ) : '—'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--admin-text-secondary)' }}>{t('admin.vehicle_requests.stat_top_desc')}</div>
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 12, boxShadow: 'var(--admin-shadow)', padding: '28px 32px' }}>
            <div style={{ fontSize: 12, color: 'var(--admin-text-secondary)', marginBottom: 10 }}>{t('admin.vehicle_requests.stat_total_votes')}</div>
            <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1, color: '#0284c7', marginBottom: 8 }}>{totalVotes}</div>
            <div style={{ fontSize: 11, color: 'var(--admin-text-secondary)' }}>{t('admin.vehicle_requests.stat_total_desc')}</div>
          </div>
        </Col>
      </Row>

      {/* Main table card */}
      <Card
        title={
          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--admin-text-primary)' }}>
            {t('admin.vehicle_requests.title')}
          </span>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchQueue}
            loading={loading}
            style={{ borderRadius: 8 }}
          >
            {t('admin.vehicle_requests.btn_refresh')}
          </Button>
        }
        className="admin-table-card"
        bordered={false}
      >
        {/* Filters */}
        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={10} md={8}>
            <Input
              placeholder={t('admin.vehicle_requests.ph_search')}
              prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
              value={filters.search}
              onChange={e => handleFilterChange({ search: e.target.value })}
              allowClear
              style={{ borderRadius: 8 }}
            />
          </Col>
          <Col xs={12} sm={7} md={5}>
            <Select
              value={filters.type}
              onChange={type => handleFilterChange({ type })}
              style={{ width: '100%', borderRadius: 8 }}
            >
              <Select.Option value="all">{t('admin.vehicle_requests.filter_all_types')}</Select.Option>
              <Select.Option value="make">{t('admin.vehicle_requests.filter_type_make')}</Select.Option>
              <Select.Option value="model">{t('admin.vehicle_requests.filter_type_model')}</Select.Option>
            </Select>
          </Col>
          <Col xs={12} sm={7} md={5}>
            <Select
              value={filters.status}
              onChange={status => handleFilterChange({ status })}
              style={{ width: '100%', borderRadius: 8 }}
            >
              <Select.Option value="all">{t('admin.vehicle_requests.filter_all_statuses')}</Select.Option>
              <Select.Option value="pending">{t('admin.vehicle_requests.filter_pending')}</Select.Option>
              <Select.Option value="approved">{t('admin.vehicle_requests.filter_approved')}</Select.Option>
              <Select.Option value="rejected">{t('admin.vehicle_requests.filter_rejected')}</Select.Option>
            </Select>
          </Col>
        </Row>

        <Table
          rowKey={r => r.id}
          loading={loading}
          columns={columns as any}
          dataSource={requests}
          className="admin-table"
          scroll={{ x: true }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total, range) => t('admin.vehicle_requests.page_summary', { from: range[0], to: range[1], total }),
            onChange: (page, pageSize) => handleTableChange(page, pageSize ?? 20),
          }}
        />
      </Card>

      {/* Reject modal */}
      <Modal
        title={t('admin.vehicle_requests.modal_title')}
        open={rejectModalOpen}
        okText={t('admin.vehicle_requests.modal_ok')}
        okButtonProps={{
          danger: true,
          loading: actionLoading,
          style: { borderRadius: 8, fontWeight: 600 },
        }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        onOk={handleReject}
        onCancel={() => {
          setRejectModalOpen(false);
          setRejectionReason('');
        }}
      >
        <div style={{ marginTop: 4 }}>
          <div style={{ fontSize: 13, color: 'var(--admin-text-secondary)', marginBottom: 12 }}>
            {t('admin.vehicle_requests.modal_desc')}
          </div>
          <Input.TextArea
            rows={4}
            placeholder={t('admin.vehicle_requests.modal_ph')}
            value={rejectionReason}
            onChange={e => setRejectionReason(e.target.value)}
            style={{ borderRadius: 8 }}
          />
        </div>
      </Modal>
    </Space>
  );
};

export default VehicleRequestQueue;
